import { Box, Stack } from "@mantine/core";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import InterestRequestsPanel, {
  type InterestRequestsPanelProps,
} from "@/pages/interests/components/InterestRequestsPanel";
import {
  toInterestRequestTableRows,
  type InterestRequestTableRow,
} from "@/pages/interests/components/InterestRequestsTable";
import PaymentMethodModal from "@/pages/interests/components/PaymentMethodModal";
import CollaborationPaymentSuccessModal from "@/pages/interests/components/CollaborationPaymentSuccessModal";
import type { useInterestRequests } from "@/pages/interests/hooks/useInterestRequests";
import type { usePreferredPartners } from "@/pages/interests/hooks/usePreferredPartners";
import { buildDashboardReturnTo, type RequestFilter } from "@/pages/dashboard/dashboardTab.const";
import { navigateToCompanyWorkspace } from "@/shared/utils/collaborationNavigation.util";
import { workspaceBackLinkState } from "@/shared/utils/workspaceBackNavigation.util";

export type InterestRequestsState = ReturnType<typeof useInterestRequests>;
export type PreferredPartnersState = ReturnType<typeof usePreferredPartners>;

type InterestRequestsTabContentProps = {
  requests: InterestRequestsState;
  preferredPartners: PreferredPartnersState;
  requestFilter: RequestFilter;
};

function panelPropsForFilter(
  requests: InterestRequestsState,
  requestFilter: RequestFilter,
  allRows: InterestRequestTableRow[]
): Pick<
  InterestRequestsPanelProps,
  | "loading"
  | "requests"
  | "isRequester"
  | "totalPages"
  | "totalCount"
  | "page"
  | "onPageChange"
  | "emptyLabel"
  | "suggestedRows"
  | "combinedRows"
> {
  if (requestFilter === "all") {
    return {
      loading: false,
      emptyLabel: "No requests or suggested companies at the moment.",
      combinedRows: allRows,
    };
  }

  if (requestFilter === "suggested") {
    return {
      loading: false,
      emptyLabel: "No suggested partner matches from GTsetu at the moment.",
    };
  }

  if (requestFilter === "sent") {
    return {
      loading: requests.loadingSent,
      requests: requests.sentRequests,
      isRequester: true,
      totalPages: requests.sentTotalPages,
      totalCount: requests.sentTotalCount,
      page: requests.sentPage,
      onPageChange: requests.setSentPage,
      emptyLabel: "No sent requests",
    };
  }

  return {
    loading: requests.loadingIncoming,
    requests: requests.incomingRequests,
    isRequester: false,
    totalPages: requests.incomingTotalPages,
    totalCount: requests.incomingTotalCount,
    page: requests.incomingPage,
    onPageChange: requests.setIncomingPage,
    emptyLabel: "No incoming requests",
  };
}

export default function InterestRequestsTabContent({
  requests,
  preferredPartners,
  requestFilter,
}: InterestRequestsTabContentProps) {
  const navigate = useNavigate();

  const allRows = useMemo(() => {
    const incoming = toInterestRequestTableRows(requests.incomingRequests, false);
    const sent = toInterestRequestTableRows(requests.sentRequests, true);
    return [...incoming, ...sent, ...preferredPartners.suggestedTableRows];
  }, [
    requests.incomingRequests,
    requests.sentRequests,
    preferredPartners.suggestedTableRows,
  ]);

  const panelProps = panelPropsForFilter(requests, requestFilter, allRows);

  const isAll = requestFilter === "all";
  const isSuggested = requestFilter === "suggested";
  const suggestedLoading = isSuggested || isAll ? preferredPartners.loading : false;
  const suggestedRows = isSuggested ? preferredPartners.suggestedTableRows : undefined;
  const combinedRows = isAll ? allRows : undefined;

  const suggestedCompanyIds = useMemo(
    () => new Set(preferredPartners.suggestedTableRows.map((r) => r.company.id)),
    [preferredPartners.suggestedTableRows]
  );

  const handleViewSuggestedProfile = useCallback(
    (companyId: string) => {
      void navigateToCompanyWorkspace(navigate, companyId, {
        state: workspaceBackLinkState(buildDashboardReturnTo("suggested")),
      });
    },
    [navigate]
  );

  const handleDismissSuggestion = useCallback(
    (recommendationId: string) => {
      void preferredPartners.dismissRecommendation(recommendationId);
    },
    [preferredPartners]
  );

  const handleViewProfile = useCallback(
    (companyId: string) => {
      if (suggestedCompanyIds.has(companyId)) {
        handleViewSuggestedProfile(companyId);
        return;
      }
      requests.handleViewProfile(companyId);
    },
    [handleViewSuggestedProfile, requests, suggestedCompanyIds]
  );

  const loading =
    isAll
      ? requests.loadingIncoming || requests.loadingSent || suggestedLoading
      : isSuggested
        ? suggestedLoading
        : panelProps.loading ?? false;

  return (
    <Stack gap="md" style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
      <Box style={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <InterestRequestsPanel
          {...panelProps}
          requestFilter={requestFilter}
          loading={loading}
          suggestedRows={suggestedRows}
          combinedRows={combinedRows}
          actionLoading={isSuggested ? null : requests.actionLoading}
          onAccept={requests.handleAccept}
          onDecline={requests.handleDecline}
          onMakePayment={requests.handleMakePayment}
          onViewProfile={isSuggested ? handleViewSuggestedProfile : handleViewProfile}
          onOpenWorkspace={requests.handleOpenWorkspace}
          onDismissSuggestion={handleDismissSuggestion}
          dismissingIds={preferredPartners.dismissingIds}
        />
      </Box>

      <PaymentMethodModal
        opened={requests.paymentModalOpened}
        onClose={requests.handlePaymentModalClose}
        onConfirm={requests.handlePaymentConfirm}
        loading={requests.paymentLoading}
        creditsRequired={requests.engagementFeeCredits}
      />
      <CollaborationPaymentSuccessModal
        opened={!!requests.successData}
        onClose={requests.handleSuccessModalClose}
        paymentData={requests.successData}
      />
    </Stack>
  );
}
